import React, { useState, useEffect } from 'react';
import { Table, Input, Typography, Button, Spin, message, Dropdown } from 'antd';
import { FileTextOutlined, CheckCircleOutlined, CloseCircleOutlined, ClockCircleOutlined, TrophyOutlined, LogoutOutlined, DownloadOutlined, FileOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import apiService from '../services/apiService';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const { Title } = Typography;
const { Search } = Input;

const Dashboard = ({ onLogout }) => {
  const navigate = useNavigate();
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [dashboardData, setDashboardData] = useState([]);
  const [loading, setLoading] = useState(true);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const data = await apiService.getDashboardData();
      console.log("data",data);
      
      // Format date to DD-MMM-YYYY format
      const formatDate = (dateString) => {
        if (!dateString) return '--';
        const date = new Date(dateString);
        const day = date.getDate().toString().padStart(2, '0');
        const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        const month = months[date.getMonth()];
        const year = date.getFullYear();
        return `${day}-${month}-${year}`;
      };
      
      // Format data and show table immediately
      const formattedData = data.map((item, index) => {
        let formattedRemarks = '--';
        if (item.remarks && typeof item.remarks === 'object') {
          const allRemarks = [
            ...(item.remarks.approved || []).map(r => r.feedback),
            ...(item.remarks.not_approved || []).map(r => r.feedback)
          ];
          formattedRemarks = allRemarks.slice(0, 3).join('; ');
          if (allRemarks.length > 3) formattedRemarks += '...';
        } else if (item.remarks && typeof item.remarks === 'string') {
          const remarkParts = item.remarks.split(';').map(r => r.trim()).filter(r => r);
          formattedRemarks = remarkParts.slice(0, 3).join('; ');
          if (remarkParts.length > 3) formattedRemarks += '...';
        }

        const poSubmitDate = formatDate(item.pr_submit_date);
        const processedOn = formatDate(item.processed_date);

        return {
          key: index + 1,
          prNo: item.pr_number,
          title: item.title || '--',
          status: item.overall_status,
          remarks: formattedRemarks,
          fullRemarks: item.remarks,
          assignedTo: item.approver_name,
          processedOn: processedOn,
          processedDate: item.processed_date,
          poSubmitDate: poSubmitDate,
          attachmentCount: item.attachment_count || 0,
          fieldAvailabilityIndex: null,
          matchedFields: 0,
          totalFields: 0,
        };
      });
      // Helper to fetch validation for a single PR
      const fetchValidationForPR = async (prNo) => {
        try {
          const validationData = await apiService.getValidationDetails(prNo);
          const results = validationData.results || validationData;

          const filteredResults = results.filter(result =>
            !result.Field.toLowerCase().includes('commodity') &&
            !result.Field.toLowerCase().includes('approval authority') &&
            !result.Field.toLowerCase().includes('pr region check') &&
            result.Field !== 'Bill To Address' &&
            result.Field !== 'Ship To Address' &&
            result.Field !== 'Bill to Address' &&
            result.Field !== 'Ship to Address' &&
            result.Field !== 'PR Region Check' &&
            result.Field !== 'PR Attachment Count Check'
          );

          const matchedCount = filteredResults.filter(result => {
            if (result.Feedback &&
                result.Feedback.includes('Not Applicable (HR PR with no attachments - Ariba data displayed for reference, no validation performed.)')) {
              return true;
            }
            if (result.Field && result.Field.toLowerCase().includes('signature') &&
                result.Feedback &&
                result.Feedback.toLowerCase().includes('not applicable') &&
                !result.Feedback.toLowerCase().includes('unhandled')) {
              return true;
            }
            const threshold = result.Field === 'Service Period/Dates' ? 0.6 : result.Field === 'CTS Entity Name' ? 0.35 : 0.5;
            if (typeof result.Similarity_Score === 'string' && result.Similarity_Score.includes(';')) {
              const scores = result.Similarity_Score.split(';')
                .map(score => parseFloat(score.trim()))
                .filter(score => !isNaN(score));
              return scores.some(score => score >= threshold);
            } else {
              const score = parseFloat(result.Similarity_Score);
              return !isNaN(score) && score >= threshold;
            }
          }).length;

          const totalCount = filteredResults.length;
          const matchPercentage = totalCount > 0 ? Math.round((matchedCount / totalCount) * 100) : 0;
          return { matchPercentage, matchedCount, totalCount };
        } catch (error) {
          console.error(`Failed to fetch validation for ${prNo}:`, error);
          return { matchPercentage: 0, matchedCount: 0, totalCount: 0 };
        }
      };

      // Fetch first 20 PRs validation immediately (visible page)
      const first20 = formattedData.slice(0, 20);
      const first20Results = await Promise.all(
        first20.map(row => fetchValidationForPR(row.prNo))
      );
      first20.forEach((row, i) => {
        row.fieldAvailabilityIndex = first20Results[i].matchPercentage;
        row.matchedFields = first20Results[i].matchedCount;
        row.totalFields = first20Results[i].totalCount;
        if (first20Results[i].matchPercentage === 100) {
          row.status = 'Recommend Approve';
        }
      });

      setDashboardData([...formattedData]);
      setLoading(false);

      // Fetch remaining PRs validation in background
      const remaining = formattedData.slice(20);
      remaining.forEach(async (row) => {
        const result = await fetchValidationForPR(row.prNo);
        setDashboardData(prev => prev.map(item =>
          item.prNo === row.prNo
            ? {
                ...item,
                fieldAvailabilityIndex: result.matchPercentage,
                matchedFields: result.matchedCount,
                totalFields: result.totalCount,
                ...(result.matchPercentage === 100 ? { status: 'Recommend Approve' } : {})
              }
            : item
        ));
      });

    } catch (error) {
      message.error('Failed to load dashboard data');
      setDashboardData([]);
    } finally {
      setLoading(false);
    }
  };

  const totalCount = dashboardData.length;
  const approvedCount = dashboardData.filter(item => item.status === 'Recommend Approve').length;
  const rejectedCount = dashboardData.filter(item => item.status === 'Recommend Reject').length;
  const onHoldCount = dashboardData.filter(item => item.status === 'Recommend Hold').length;
  const acceptanceRate = ((approvedCount / totalCount) * 100).toFixed(1);

  const getFilteredData = () => {
    let filtered = dashboardData.filter(item => {
      const matchesSearch = item.prNo.toLowerCase().includes(searchText.toLowerCase()) ||
                           item.title.toLowerCase().includes(searchText.toLowerCase()) ||
                           item.assignedTo.toLowerCase().includes(searchText.toLowerCase());
      const matchesStatus = statusFilter === 'All' || item.status === statusFilter;
      return matchesSearch && matchesStatus;
    });
    return filtered.sort((a, b) => a.prNo.localeCompare(b.prNo));
  };

  const getStatusTag = (status) => {
    const statusConfig = {
      'Recommend Approve': { color: '#16a34a', bg: '#dcfce7' },
      'Recommend Reject': { color: '#dc2626', bg: '#fee2e2' },
      'Recommend Hold': { color: '#ea580c', bg: '#fed7aa' },
      'Yet to be Processed': { color: '#6b7280', bg: '#f3f4f6' }
    };
    
     const config = statusConfig[status] || { color: '#6b7280', bg: '#dae6f5ff' };
  return (
    <span className="status-tag" style={{ color: config.color, backgroundColor: config.bg }}>
      {status}
    </span>
  );
};

  const getRowClassName = (record) => {
    const baseClass = 'table-row';
    const statusClass = {
      'Recommend Approve': 'row-approved',
      'Recommend Reject': 'row-rejected',
      'Recommend Hold': 'row-onhold',
      'Yet to be Processed': 'row-pending'
    }[record.status];
    return `${baseClass} ${statusClass}`;
  };

  const handlePRClick = (prNo) => {
    const prData = dashboardData.find(item => item.prNo === prNo);
    navigate(`/pr-details/${prNo}`, { state: { remarks: prData?.fullRemarks } });
  };

  const handleCardClick = (status) => {
    setStatusFilter(status === statusFilter ? 'All' : status);
  };

  const handleLogout = () => {
    if (onLogout) {
      onLogout();
      navigate('/login', { replace: true });
    }
  };

  const downloadCSV = () => {
    const filteredData = getFilteredData();
    const csvData = filteredData.map((item, index) => ({
      'S.No': index + 1,
      'PR Number': `'${item.prNo}`,
      'PR Title': item.title,
      'Field-Availability Index': `${item.matchedFields}/${item.totalFields} (${item.fieldAvailabilityIndex}%)`,
      'AI Recommendation': item.status,
      'Remarks': item.remarks,
      'PR Submit Date': `'${item.poSubmitDate}`,
      'AI Processed Date': `'${item.processedOn}`,
      'Assigned To': item.assignedTo
    }));
    
    const ws = XLSX.utils.json_to_sheet(csvData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Dashboard Data');
    XLSX.writeFile(wb, 'dashboard-data.csv');
    message.success('CSV file downloaded successfully');
  };

  const downloadExcel = () => {
    const filteredData = getFilteredData();
    const excelData = filteredData.map((item, index) => ({
      'S.No': index + 1,
      'PR Number': item.prNo,
      'PR Title': item.title,
      'Field-Availability Index': `${item.matchedFields}/${item.totalFields} (${item.fieldAvailabilityIndex}%)`,
      'AI Recommendation': item.status,
      'Remarks': item.remarks,
      'PR Submit Date': item.poSubmitDate,
      'AI Processed Date': item.processedOn,
      'Assigned To': item.assignedTo
    }));
    
    const ws = XLSX.utils.json_to_sheet(excelData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Dashboard Data');
    XLSX.writeFile(wb, 'dashboard-data.xlsx');
    message.success('Excel file downloaded successfully');
  };

  const downloadPDF = () => {
    const filteredData = getFilteredData();
    const doc = new jsPDF();
    
    doc.setFontSize(16);
    doc.text('Low Value Purchase Requests Dashboard', 14, 22);
    
    const tableData = filteredData.map((item, index) => [
      index + 1,
      item.prNo,
      item.title,
      `${item.matchedFields}/${item.totalFields} (${item.fieldAvailabilityIndex}%)`,
      item.status,
      item.remarks,
      item.poSubmitDate,
      item.processedOn,
      item.assignedTo
    ]);
    
    autoTable(doc, {
      head: [['S.No', 'PR Number', 'PR Title', 'Field-Availability Index', 'AI Recommendation', 'Remarks', 'PR Submit Date', 'AI Processed Date', 'Assigned To']],
      body: tableData,
      startY: 30,
      styles: { fontSize: 8 },
      headStyles: { fillColor: [41, 128, 185] },
      columnStyles: {
        0: { cellWidth: 12 },
        1: { cellWidth: 20 },
        2: { cellWidth: 25 },
        3: { cellWidth: 22 },
        4: { cellWidth: 25 },
        5: { cellWidth: 30 },
        6: { cellWidth: 20 },
        7: { cellWidth: 20 },
        8: { cellWidth: 20 }
      }
    });
    
    doc.save('dashboard-data.pdf');
    message.success('PDF file downloaded successfully');
  };

  const downloadMenuItems = [
    {
      key: 'csv',
      label: 'Download CSV',
      onClick: downloadCSV
    },
    {
      key: 'excel',
      label: 'Download Excel',
      onClick: downloadExcel
    },
    {
      key: 'pdf',
      label: 'Download PDF',
      onClick: downloadPDF
    }
  ];

  const columns = [
    {
      title: 'S.No',
      key: 'sno',
      width: 50,
      align: 'left',
      render: (_, __, index) => (currentPage - 1) * pageSize + index + 1,
    },
    {
      title: 'PR Number',
      dataIndex: 'prNo',
      key: 'prNo',
      width: 100,
      render: (text, record) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          {record.attachmentCount > 0 && (
            <FileOutlined style={{ color: '#1890ff', fontSize: '12px' }} />
          )}
          <Button 
            type="link" 
            className="pr-link"
            onClick={() => handlePRClick(text)}
          >
            {text}
          </Button>
        </div>
      ),
    },
    {
      title: 'PR Title',
      dataIndex: 'title',
      key: 'title',
      width: 130,
      ellipsis: true,
    },
    {
      title: 'Field-Availability Index',
      key: 'fieldAvailabilityIndex',
      width: 150,
      align: 'center',
      render: (_, record) => {
        const { matchedFields, totalFields, fieldAvailabilityIndex } = record;
        if (fieldAvailabilityIndex === null) {
          return <span style={{ color: '#999', fontSize: '13px' }}>Loading...</span>;
        }
        const getScoreColor = (percentage) => {
          if (percentage >= 80) return "#52c41a";
          if (percentage >= 50) return "#faad14";
          return "#ff4d4f";
        };
        return (
          <span style={{
            color: getScoreColor(fieldAvailabilityIndex),
            fontWeight: '600',
            fontSize: '13px'
          }}>
            {matchedFields}/{totalFields} ({fieldAvailabilityIndex}%)
          </span>
        );
      },
      sorter: (a, b) => (a.fieldAvailabilityIndex || 0) - (b.fieldAvailabilityIndex || 0),
    },
    {
      title: 'AI Recommendation',
      dataIndex: 'status',
      key: 'status',
      width: 170,
      align: 'left',
      render: (status) => getStatusTag(status),
    },
     {
      title: 'PR Submit Date',
      dataIndex: 'poSubmitDate',
      key: 'submitDate',
      width: 100,
      align: 'left',
    },
    {
      title: 'AI Processed Date',
      dataIndex: 'processedOn',
      key: 'processedOn',
      width: 120,
      align: 'left',
    },
    {
      title: 'Assigned To',
      dataIndex: 'assignedTo',
      key: 'assignedTo',
      width: 140,
      align: 'left',
    },
    {
      title: 'Decision Date',
      key: 'elapsedDate',
      width: 100,
      align: 'left',
      render: (_, record) => {
        const elapsedDates = ['25-Feb-2025', '23-Feb-2025', '24-Feb-2025', '22-Feb-2025', '26-Feb-2025'];
        const hash = record.prNo.split('').reduce((a, b) => a + b.charCodeAt(0), 0);
        return elapsedDates[hash % elapsedDates.length];
      },
    },
    {
      title: 'Elapsed Days',
      key: 'elapsedDays',
      width: 90,
      align: 'left',
      render: (_, record) => {
        if (!record.processedDate || record.processedDate === '--') return '--';
        const today = new Date();
        const processedDate = new Date(record.processedDate);
        const diffTime = Math.abs(today - processedDate);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        return `${diffDays} days`;
      },
    },
  
    
  ];

  return (
    <div className="enterprise-dashboard">
      <Spin spinning={loading} tip="Loading dashboard data...">
      <div className="page-header">
        <Title level={3} className="page-title">
          Low Value Purchase Requests Processing
        </Title>
        <Button 
          type="primary" 
          icon={<LogoutOutlined />} 
          onClick={handleLogout}
          className="logout-btn"
        >
          Logout
        </Button>
      </div>

      <div className="summary-cards">
        <div 
          className={`summary-card total-card ${statusFilter === 'All' ? 'active' : ''}`}
          onClick={() => setStatusFilter('All')}
        >
          <div className="card-icon">
            <FileTextOutlined />
          </div>
          <div className="card-content">
            <div className="card-value">{totalCount}</div>
            <div className="card-title">Total Purchase Requests</div>
          </div>
        </div>
        
        <div 
          className={`summary-card approved-card ${statusFilter === 'Recommend Approve' ? 'active' : ''}`}
          onClick={() => handleCardClick('Recommend Approve')}
        >
          <div className="card-icon row-approved">
            <CheckCircleOutlined />
          </div>
          <div className="card-content">
            <div className="card-value">{approvedCount}</div>
            <div className="card-title">Recommend Approve</div>
          </div>
        </div>
        
        <div 
          className={`summary-card rejected-card ${statusFilter === 'Recommend Rejected' ? 'active' : ''}`}
          onClick={() => handleCardClick('Recommend Rejected')}
        >
          <div className="card-icon">
            <CloseCircleOutlined />
          </div>
          <div className="card-content">
            <div className="card-value">{rejectedCount}</div>
            <div className="card-title">Recommend Rejected</div>
          </div>
        </div>
        
        <div 
          className={`summary-card onhold-card ${statusFilter === 'Recommend Hold' ? 'active' : ''}`}
          onClick={() => handleCardClick('Recommend Hold')}
        >
          <div className="card-icon">
            <ClockCircleOutlined />
          </div>
          <div className="card-content">
            <div className="card-value">{onHoldCount}</div>
            <div className="card-title">Recommend Hold</div>
          </div>
        </div>
        
        {/* <div 
          className={`summary-card pending-card ${statusFilter === 'Yet to be Processed' ? 'active' : ''}`}
          onClick={() => handleCardClick('Yet to be Processed')}
        >
          <div className="card-icon">
            <ClockCircleOutlined />
          </div>
          <div className="card-content">
            <div className="card-value">{yetToProcessCount}</div>
            <div className="card-title">Yet to be Processed</div>
          </div>
        </div> */}
        
        <div className="summary-card rate-card">
          <div className="card-icon">
            <TrophyOutlined />
          </div>
          <div className="card-content">
            <div className="card-value">{acceptanceRate}%</div>
            <div className="card-title">Acceptance Rate</div>
          </div>
        </div>
      </div>

      <div className="table-section">
        <div className="controls-bar" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Search
            placeholder="Search PR Number, Title or Assigned To.."
            allowClear
            className="search-input"
            variant='borderless'
            onChange={(e) => setSearchText(e.target.value)}
            style={{ maxWidth: '400px' }}
          />
          <Dropdown
            menu={{ items: downloadMenuItems }}
            placement="bottomRight"
            trigger={['click']}
          >
            <Button type="primary" icon={<DownloadOutlined />}>
              Download
            </Button>
          </Dropdown>
        </div>
        
        <div className="table-container">
          <Table
            columns={columns}
            dataSource={getFilteredData()}
            pagination={{
              current: currentPage,
              pageSize: pageSize,
              showSizeChanger: true,
              pageSizeOptions: ['10', '20', '30', '40'],
              showQuickJumper: false,
              showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`,
              size: 'small',
              onChange: (page, size) => {
                setCurrentPage(page);
                setPageSize(size);
              },
              onShowSizeChange: (current, size) => {
                setCurrentPage(1);
                setPageSize(size);
              }
            }}
            className="data-table"
            rowClassName={getRowClassName}
            size="small"
          />
        </div>
      </div>
      </Spin>
    </div>
  );
};

export default Dashboard;